/* React base class */
import { Component } from "react"

/* Redux store connection */
import { connect } from "react-redux"

/* Router helper */
import { withRouter, applySeo } from "../helpers"

/* UI components */
import { Block, Card, Loading, Network } from "../components"

/* Icon component */
import Icon from "../components/Icon"

/* Filter dropdown component */
import FilterDropdown from "../components/FilterDropdown"

/* Map view component */
import MapView from "../components/MapView"

/* Organization list API call */
import { list } from "../api/Organization"

/* Translation */
import { translate } from "../translates"

/* Page Search */
class Search extends Component {

    constructor(props) {
        super(props)
        /* State */
        this.state = {
            loading: true,
            loadingMore: false,
            error: false,
            items: [],
            mapItems: [],
            total: 0,
            page: 1,
            view: "grid",
            sort: "rating",
            near: false,
            geoDenied: false,
            price: "any",
            rating: "any",
            hours: "any",
            lat: null,
            lng: null
        }
    }

    componentDidMount = () => {
        /* SEO */
        const params = new URLSearchParams(window.location.search)
        const query = params.get("q")
        applySeo({
            title: query ? `«${query}» — поиск заведений | Bookify` : "Каталог заведений — онлайн-запись | Bookify",
            description: query ? `Результаты поиска «${query}» в Bookify — салоны, барбершопы и сервисы Казахстана с онлайн-записью.` : "Каталог салонов красоты, барбершопов, студий маникюра, автомоек и других сервисов Казахстана. Фильтры, отзывы и онлайн-запись в Bookify.",
            keywords: "каталог салонов, поиск мастера, запись онлайн, салоны красоты, барбершоп, маникюр, автомойка, Bookify",
            path: window.location.pathname + window.location.search
        })
        window.scroll(0, 0)
        this._detectLocation()
    }

    componentDidUpdate = (prev) => {
        const prevCityId = prev.city ? prev.city.id : null
        const nextCityId = this.props.city ? this.props.city.id : null
        if (prev.location.search !== this.props.location.search || prevCityId !== nextCityId) {
            this.setState({ page: 1 }, this._applyFilters)
        }
    }

    /* Reload the list and, if the map is open, its markers too */
    /* Draw Apply Filters */
    _applyFilters = () => {
        this.load()
        if (this.state.view === "map") {
            this._loadMap()
        }
    }

    /* Fetch every matching organization for the map markers (no pagination) */
    /* Draw Load Map */
    _loadMap = () => {
        list({ ...this._params(), map: 1 }).then((response) => {
            if (response && response.status === 200) {
                this.setState({ mapItems: response.data.data || [] })
            }
        })
    }

    /* Turn on "Рядом" automatically when geolocation is available */
    /* Draw Detect Location */
    _detectLocation = () => {
        if (!navigator.geolocation) {
            this.load()
            return
        }
        navigator.geolocation.getCurrentPosition(
            (position) => this.setState({ near: true, sort: "near", lat: position.coords.latitude, lng: position.coords.longitude }, this.load),
            () => this.load(),
            { timeout: 5000 }
        )
    }

    /* Draw Params */
    _params = () => {
        const search = new URLSearchParams(this.props.location.search)
        const params = { sort: this.state.sort, page: this.state.page, limit: 12 }
        if (search.get("q")) {
            params.q = search.get("q")
        }
        if (search.get("category")) {
            params.category = search.get("category")
        }
        if (this.props.city) {
            params.city = this.props.city.id
        }
        if (this.state.price !== "any") {
            params.price = this.state.price
        }
        if (this.state.rating !== "any") {
            params.rating = this.state.rating
        }
        if (this.state.hours === "open") {
            params.openNow = true
        }
        if (this.state.lat && this.state.lng) {
            params.lat = this.state.lat
            params.lng = this.state.lng
        }
        return params
    }

    load = (append = false) => {
        this.setState(append ? { loadingMore: true } : { loading: true, error: false })
        list(this._params()).then((response) => {
            if (response && response.status === 200) {
                const rows = response.data.data || []
                const total = response.data.meta ? response.data.meta.total : 0
                this.setState((prev) => ({
                    items: append ? [...prev.items, ...rows] : rows,
                    total,
                    loading: false,
                    loadingMore: false
                }))
            }
            else {
                this.setState({ loading: false, loadingMore: false, error: true })
            }
        })
    }

    /* Reload from the first page after a filter change */
    /* Draw Reload */
    _reload = () => {
        this.setState({ page: 1 }, this._applyFilters)
    }

    /* Draw Load More */
    _loadMore = () => {
        this.setState((prev) => ({ page: prev.page + 1 }), () => this.load(true))
    }

    /* Draw Toggle Near */
    _toggleNear = () => {
        if (this.state.near) {
            this.setState({ near: false, geoDenied: false, sort: "rating", lat: null, lng: null, page: 1 }, this._applyFilters)
            return
        }
        if (!navigator.geolocation) {
            this.setState({ geoDenied: true })
            return
        }
        navigator.geolocation.getCurrentPosition(
            (position) => this.setState({ near: true, geoDenied: false, sort: "near", lat: position.coords.latitude, lng: position.coords.longitude, page: 1 }, this._applyFilters),
            () => this.setState({ geoDenied: true, near: false })
        )
    }

    /* Draw Reset All */
    _resetAll = () => {
        this.setState({ near: false, geoDenied: false, sort: "rating", lat: null, lng: null, price: "any", rating: "any", hours: "any", page: 1 }, this._applyFilters)
    }

    /* Map view — full-width map with a marker per organization */
    /* Draw Map View */
    _mapView = () => {
        return (
            <div className="mapview">
                <MapView items={this.state.mapItems} onSelect={(id) => this.props.navigate(`/organization/${id}`)} />
            </div>
        )
    }

    /* Grid view with "load more" pagination */
    /* Draw Grid View */
    _gridView = () => {
        /* Fields */
        const { items, total, loadingMore } = this.state
        return (
            <>
                <div className="grid grid--3" style={{ marginTop: 16 }}>
                    {items.map((s) => (
                        <Card key={`${s.id}`} s={s} />
                    ))}
                    {items.length === 0 && (
                        <div style={{ gridColumn: "1 / -1", padding: "48px 0", textAlign: "center", color: "var(--muted)" }}>
                            {translate("По выбранным фильтрам ничего не найдено.")}{" "}
                            <button onClick={this._resetAll} style={{ color: "var(--accent)", fontWeight: 700 }}>{translate("Сбросить фильтры")}</button>
                        </div>
                    )}
                </div>
                {items.length > 0 && items.length < total && (
                    <div className="loadmore">
                        <button className="btn btn--ghost btn--lg" disabled={loadingMore} onClick={this._loadMore}>
                            {loadingMore ? translate("Загрузка…") : `${translate("Показать ещё")} (${total - items.length})`}
                        </button>
                    </div>
                )}
            </>
        )
    }

    render = () => {
        const search = new URLSearchParams(this.props.location.search)
        const categories = this.props.data && Array.isArray(this.props.data.categories) ? this.props.data.categories : []
        const catObj = categories.find((c) => `${c.id}` === search.get("category"))
        const cat = catObj ? translate(catObj.name) : (search.get("q") || translate("Все услуги"))
        const cityName = this.props.city ? this.props.city.name : translate("Алматы")

        const activeCount =
            (this.state.near ? 1 : 0) + (this.state.price !== "any" ? 1 : 0) + (this.state.rating !== "any" ? 1 : 0) +
            (this.state.hours !== "any" ? 1 : 0)

        return (
            <Block>
                <div className="page">
                    <div className="container">
                        <div style={{ display: "flex", alignItems: "center", gap: 12, color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
                            <a onClick={() => this.props.navigate("/")} style={{ cursor: "pointer" }}>{translate("Главная")}</a>
                            <Icon name="chevron_right" size={12} />
                            <span>{translate("Услуги")}</span>
                            <Icon name="chevron_right" size={12} />
                            <span style={{ color: "var(--ink)" }}>{cat}</span>
                        </div>

                        <div style={{ display: "flex", alignItems: "end", justifyContent: "space-between", marginBottom: 8, gap: 24 }}>
                            <div>
                                <h1 className="page__title">{cat} {translate("в")} {cityName}</h1>
                                <p className="page__subtitle">{translate("Найдено")} {this.state.total} {translate("организаций")}</p>
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button className={`filter filter--icon ${this.state.view === "grid" ? "is-active" : ""}`} onClick={() => this.setState({ view: "grid" })} title={translate("Списком")}>
                                    <Icon name="sliders" size={16} />
                                </button>
                                <button className={`filter filter--icon ${this.state.view === "map" ? "is-active" : ""}`} onClick={() => this.setState({ view: "map" }, this._loadMap)} title={translate("На карте")}>
                                    <Icon name="map" size={16} />
                                </button>
                            </div>
                        </div>

                        <div className="filterbar">
                            <button className={`filter ${this.state.near ? "is-active" : ""}`} onClick={this._toggleNear}>
                                <Icon name="pin" size={14} /> {translate("Рядом")}
                            </button>
                            <FilterDropdown label={translate("Цена")} value={this.state.price} onChange={(v) => this.setState({ price: v }, this._reload)}
                                options={[{ id: "any", label: "Любая цена" }, { id: "1", label: "₸ · недорого" }, { id: "2", label: "₸₸ · средне" }, { id: "3", label: "₸₸₸ · премиум" }]} />
                            <FilterDropdown label={translate("Рейтинг")} value={this.state.rating} onChange={(v) => this.setState({ rating: v }, this._reload)}
                                options={[{ id: "any", label: "Любой рейтинг" }, { id: "4.5", label: "от 4.5 ★" }, { id: "4.7", label: "от 4.7 ★" }, { id: "4.9", label: "от 4.9 ★" }]} />
                            <FilterDropdown label={translate("Время работы")} value={this.state.hours} onChange={(v) => this.setState({ hours: v }, this._reload)}
                                options={[{ id: "any", label: "Любое время" }, { id: "open", label: "Открыто сейчас" }]} />
                            <div className="filterbar__spacer" />
                            <span className="filterbar__sortlabel">{translate("Сортировка:")}</span>
                            <FilterDropdown label={translate("По рейтингу")} value={this.state.sort} onChange={(v) => this.setState({ sort: v }, this._reload)}
                                options={[{ id: "rating", label: "По рейтингу" }, { id: "near", label: "По расстоянию" }, { id: "reviews", label: "По отзывам" }, { id: "price", label: "По цене" }]} />
                            {activeCount > 0 && (
                                <button className="filter filter--icon" style={{ position: "relative" }} onClick={this._resetAll} title={translate("Сбросить фильтры")}>
                                    <Icon name="trash" size={16} />
                                    <span className="filter__count">{activeCount}</span>
                                </button>
                            )}
                        </div>

                        {this.state.geoDenied && (
                            <div className="geo-note">
                                <Icon name="pin" size={16} />
                                <span>{translate("Нет доступа к геолокации. Разрешите доступ в браузере, чтобы видеть организации рядом — пока показываем по рейтингу.")}</span>
                                <button onClick={() => this.setState({ geoDenied: false })} aria-label={translate("Закрыть")}><Icon name="close" size={14} /></button>
                            </div>
                        )}

                        {this.state.view === "map" ? (
                            this._mapView()
                        ) : this.state.loading ? (
                            <Loading />
                        ) : this.state.error ? (
                            <Network onRetry={this.load} />
                        ) : (
                            this._gridView()
                        )}
                    </div>
                </div>
            </Block>
        )
    }
}

/* Map State To Props */
const mapStateToProps = state => {
    return {
        city: state.city,
        data: state.data,
        lang: state.lang
    }
}

/* Export */
export default connect(mapStateToProps)(withRouter(Search))
